/**
 * Состояние выгрузки операций кассового агента на сервер: `GET /v1/upload` агента (RP-OWN9). Это ДИАГНОСТИКА: она не участвует в
 * решении «можно ли продавать» и не должна мешать денежной операции. Поэтому чтение вынесено в отдельный монитор с редким
 * интервалом, который уступает дорогу активной денежной операции и не опрашивает агента чаще порога.
 *
 * Чистый разбор и монитор без React и без сети: чтение приходит снаружи (`read`), часы и таймеры подменяются в тестах.
 */
import type { LocalAgentClient } from './localAgent'

/** Начиная с этого `api.level` агент отдаёт `/v1/upload` (выгрузка введена вместе с фискальными маршрутами). */
export const UPLOAD_MIN_API_LEVEL = 4

/** Признак в `api.features`, которым агент может объявить выгрузку отдельно от уровня. */
export const UPLOAD_FEATURE = 'upload'

/** Как часто читаем состояние выгрузки по умолчанию, мс. Редко: это диагностика, а не часть оплаты. */
export const UPLOAD_READ_EVERY_MS = 60_000
/** Нижний предел между двумя запросами к агенту, мс: частое чтение, в том числе ручное «Проверить», игнорируется. */
export const UPLOAD_MIN_GAP_MS = 15_000
/** Через сколько повторить, если чтение отложено из-за денежной операции, мс. */
export const UPLOAD_DEFER_MS = 5_000

/** Агент (по `hello.api`) умеет отдавать состояние выгрузки. */
export function uploadSupported(api: { level: number; features: readonly string[] } | null | undefined): boolean {
  if (api === null || api === undefined) return false
  return api.level >= UPLOAD_MIN_API_LEVEL || api.features.includes(UPLOAD_FEATURE)
}

/** Последний отказ сервера, как его запомнил агент (`outbox-rejected`). Текст — дословно, с номером запроса в конце. */
export interface UploadRejection {
  operationId: string
  revision: number
  status: number | null
  code: string | null
  text: string
  at: string
  /** Номер запроса сервера (`X-Request-Id`), вынутый из текста агента «(запрос …)»; `null` — агент его не записал. */
  requestId: string | null
}

export interface UploadStatus {
  /** `false` — адрес выгрузки (`Upload:Url`) пуст: очередь копится, но не уходит. */
  enabled: boolean
  /** Сколько записей ждёт отправки. */
  pending: number
  /** Сколько записей сервер отклонил (в `outbox-rejected`). */
  rejected: number
  lastSuccessAt: string | null
  lastError: string | null
  oldestPendingAt: string | null
  /** Сколько записей оставлено в очереди, потому что сервер ждёт пропущенную ревизию. */
  waitingRevision: number
  lastRejected: UploadRejection | null
}

const REQUEST_ID = /\(запрос\s+([^)\s]+)\)/u

const asString = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null)
const asCount = (value: unknown, field: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new Error(`Агент вернул состояние выгрузки без числа «${field}».`)
  return Math.trunc(value)
}

function decodeRejection(raw: unknown): UploadRejection | null {
  if (typeof raw !== 'object' || raw === null) return null
  const value = raw as Record<string, unknown>
  const text = typeof value.text === 'string' ? value.text : ''
  return {
    operationId: asString(value.operationId) ?? '',
    revision: typeof value.revision === 'number' ? value.revision : 0,
    status: typeof value.status === 'number' ? value.status : null,
    code: asString(value.code),
    text,
    at: asString(value.at) ?? '',
    requestId: REQUEST_ID.exec(text)?.[1] ?? null,
  }
}

/** Разбор ответа `GET /v1/upload`. Поля без значения агент не присылает (null опускается). */
export function decodeUploadStatus(raw: unknown): UploadStatus {
  if (typeof raw !== 'object' || raw === null) throw new Error('Агент вернул состояние выгрузки в неожиданном виде.')
  const value = raw as Record<string, unknown>
  if (typeof value.enabled !== 'boolean') throw new Error('Агент вернул состояние выгрузки без признака «enabled».')
  return {
    enabled: value.enabled,
    pending: asCount(value.pending, 'pending'),
    rejected: asCount(value.rejected, 'rejected'),
    lastSuccessAt: asString(value.lastSuccessAt),
    lastError: asString(value.lastError),
    oldestPendingAt: asString(value.oldestPendingAt),
    waitingRevision: value.waitingRevision === undefined ? 0 : asCount(value.waitingRevision, 'waitingRevision'),
    lastRejected: decodeRejection(value.lastRejected),
  }
}

/** Одно чтение: только GET, ничего не меняет. Токен рабочего места нужен, как для фискальных чтений. */
export function readUploadStatus(client: LocalAgentClient, port: number, token: string): Promise<UploadStatus> {
  return client.request({ port, path: '/v1/upload', token, decode: decodeUploadStatus, timeoutMs: 5_000 })
}

/**
 * Что делал монитор. Четыре разных вещи, которые нельзя смешивать на экране:
 * `sending` — запрос ушёл, ответа ещё нет; `answered` — ответ получен (время `readAt`); `failed` — агент не ответил или отказал
 * (повторная проверка запланирована); `deferred` — чтение отложено, потому что идёт денежная операция.
 */
export type UploadReadPhase = 'never' | 'sending' | 'answered' | 'failed' | 'deferred'

export interface UploadSnapshot {
  phase: UploadReadPhase
  /** Последний полученный ответ агента (может быть старым, если после него были сбои). */
  status: UploadStatus | null
  /** Когда получен `status`, мс (Unix). */
  readAt: number | null
  /** Когда запланирована следующая проверка, мс (Unix); `null` — монитор остановлен. */
  nextAt: number | null
  /** Сколько проверок подряд не удалось. */
  failures: number
  /** Причина последнего сбоя — словами агента или клиента. */
  failure: string | null
}

export interface UploadMonitorOptions {
  /** Одно чтение состояния; бросает ошибку с русским текстом. */
  read: () => Promise<UploadStatus>
  /** `true`, пока идёт денежная операция (чек, смена, наличные, неизвестный исход): диагностика уступает. */
  isMoneyBusy: () => boolean
  onChange: (snapshot: UploadSnapshot) => void
  everyMs?: number
  minGapMs?: number
  deferMs?: number
  clock?: () => number
}

export interface UploadMonitor {
  start: () => void
  stop: () => void
  /** Проверить сейчас (кнопка «Проверить»). Не чаще `minGapMs`: слишком частый вызов ничего не отправляет и возвращает `false`. */
  refresh: () => boolean
  getSnapshot: () => UploadSnapshot
}

export function createUploadMonitor(options: UploadMonitorOptions): UploadMonitor {
  const clock = options.clock ?? (() => Date.now())
  const every = Math.max(options.everyMs ?? UPLOAD_READ_EVERY_MS, options.minGapMs ?? UPLOAD_MIN_GAP_MS)
  const minGap = options.minGapMs ?? UPLOAD_MIN_GAP_MS
  const defer = options.deferMs ?? UPLOAD_DEFER_MS
  let snapshot: UploadSnapshot = { phase: 'never', status: null, readAt: null, nextAt: null, failures: 0, failure: null }
  let timer: ReturnType<typeof setTimeout> | undefined
  let running = false
  let inFlight = false
  let lastSentAt = Number.NEGATIVE_INFINITY
  let generation = 0

  const publish = (patch: Partial<UploadSnapshot>): void => {
    snapshot = { ...snapshot, ...patch }
    options.onChange(snapshot)
  }

  const schedule = (afterMs: number): void => {
    if (timer !== undefined) clearTimeout(timer)
    if (!running) return
    publish({ nextAt: clock() + afterMs })
    timer = setTimeout(() => void tick(), afterMs)
  }

  async function tick(): Promise<void> {
    timer = undefined
    if (!running || inFlight) return
    if (options.isMoneyBusy()) {
      // Денежная операция важнее диагностики: запрос не отправляется, ждём коротко и спрашиваем снова.
      publish({ phase: 'deferred' })
      schedule(defer)
      return
    }
    if (clock() - lastSentAt < minGap) {
      schedule(minGap - (clock() - lastSentAt))
      return
    }
    inFlight = true
    lastSentAt = clock()
    const mine = generation
    publish({ phase: 'sending', nextAt: null })
    try {
      const status = await options.read()
      if (mine !== generation) return
      publish({ phase: 'answered', status, readAt: clock(), failures: 0, failure: null })
    } catch (reason) {
      if (mine !== generation) return
      const text = reason instanceof Error && reason.message !== '' ? reason.message : 'Агент не ответил на запрос состояния выгрузки.'
      publish({ phase: 'failed', failures: snapshot.failures + 1, failure: text })
    } finally {
      inFlight = false
    }
    schedule(every)
  }

  return {
    start() {
      if (running) return
      running = true
      generation += 1
      void tick()
    },
    stop() {
      running = false
      generation += 1
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
    },
    refresh() {
      if (!running || inFlight || clock() - lastSentAt < minGap) return false
      if (timer !== undefined) clearTimeout(timer)
      void tick()
      return true
    },
    getSnapshot: () => snapshot,
  }
}
